/**
 * Single source of truth for the locales the learner app supports.
 *
 * Wave 8 (ROADS-i18n-infra) — replaces the previously hardcoded `["en", "es"]`
 * tuples scattered across `i18n.ts`, the `LanguageSwitcher`, and ad-hoc tests.
 *
 * To add a new language X:
 *   1. Drop `apps/web/src/locales/X/translation.json` (mirroring the EN key
 *      tree).
 *   2. Add an entry to `LOCALE_DEFINITIONS` below.
 *   3. Add a `import X from "@/locales/X/translation.json"` line and resource
 *      registration in `apps/web/src/i18n.ts`.
 *
 * The language switcher and browser-language detection both consume this file
 * directly, so step 2 is the only chrome-side change needed.
 *
 * For the "why" — see `docs/i18n.md` (workflow and parity test details).
 */

/**
 * BCP-47 tag for any locale registered (GA or pending). We intentionally
 * use the simple primary subtag (`en`, `es`, `hi`, `tl`) for languages with
 * a single regional variant we currently care about, and the full tag
 * (`zh-CN`) where the script / region matters at the writing-system level.
 *
 * `LocaleCode` covers BOTH GA and pending entries. The narrower
 * `SupportedLocaleCode` union is the GA-only subset and is what runtime
 * resolvers (`detectInitialLocale`, `matchSupportedLocale`) return.
 */
export type LocaleCode =
  | "en"
  | "es"
  | "hi"
  | "zh-CN"
  | "tl"
  | "tr"
  | "vi";

/**
 * Subset of `LocaleCode` whose `status` is `"ga"` in `LOCALE_DEFINITIONS`.
 * Hand-maintained: TypeScript can't narrow `.filter()` results back to a
 * literal union, so flipping a definition from `pending` → `ga` is paired
 * with extending this union. The locale-parity test (which iterates
 * `LOCALE_DEFINITIONS` at runtime) catches forgetting one.
 */
export type SupportedLocaleCode = "en" | "es" | "hi" | "zh-CN" | "tl" | "tr";

interface LocaleDefinition {
  /** BCP-47 code used by i18next, the URL, and `<html lang>`. */
  readonly code: LocaleCode;
  /**
   * The locale name in English. Used in admin / debug surfaces only;
   * user-facing language pickers should prefer `nativeLabel`.
   */
  readonly englishLabel: string;
  /**
   * The locale name written in its own script. This is what the language
   * switcher renders.
   */
  readonly nativeLabel: string;
  /**
   * Short uppercase tag shown next to the native label in the language
   * switcher (e.g. "EN", "ES", "HI", "ZH"). Matches the marketing site's
   * `shortLabel` so both surfaces display consistently.
   */
  readonly shortLabel: string;
  /** Writing direction. Today only RTL is `ltr`; reserved for ar/he/fa/ur. */
  readonly dir: "ltr" | "rtl";
  /**
   * Two-letter region for the flag glyph if the design ever wants one. Kept
   * abstract (not a flag emoji) so the design system can swap in a real
   * SVG flag later. May be omitted for languages without a single
   * canonical flag (e.g. `tl` — Filipino is spoken across many regions).
   */
  readonly flagRegion?: string;
  /**
   * Lifecycle gate.
   *
   *   - `"ga"`         — translation.json exists, parity-tested, surfaced in
   *                      the language switcher.
   *   - `"pending"`    — slot reserved (so concurrent translation work has
   *                      a stable place to land), but the locale is hidden
   *                      from the user-facing switcher and exempt from the
   *                      parity test until the JSON file lands.
   *
   * Wave-8 ratchet: Slot A/B/C flip `pending` → `ga` in the same PR that
   * adds their translation file. That single-line flip is the entire
   * promotion ceremony — `docs/i18n.md` documents it.
   */
  readonly status: "ga" | "pending";
}

/**
 * Authoritative locale list. Order is the order shown in the language
 * switcher dropdown — keep `en` first, then alphabetic by `englishLabel`
 * unless a designer overrides it.
 */
export const LOCALE_DEFINITIONS = [
  { code: "en", englishLabel: "English", nativeLabel: "English", shortLabel: "EN", dir: "ltr", flagRegion: "US", status: "ga" },
  { code: "es", englishLabel: "Spanish", nativeLabel: "Español", shortLabel: "ES", dir: "ltr", flagRegion: "ES", status: "ga" },
  // Wave 8 — all 3 bundles landed; flipped to "ga" during D's rebase onto post-A/C main.
  { code: "hi", englishLabel: "Hindi", nativeLabel: "हिन्दी", shortLabel: "HI", dir: "ltr", flagRegion: "IN", status: "ga" },
  { code: "zh-CN", englishLabel: "Simplified Chinese", nativeLabel: "简体中文", shortLabel: "ZH", dir: "ltr", flagRegion: "CN", status: "ga" },
  { code: "tl", englishLabel: "Filipino", nativeLabel: "Filipino", shortLabel: "TL", dir: "ltr", flagRegion: "PH", status: "ga" },
  // Wave 10 — Turkish (Türkçe) launched as the 6th GA UI locale. NJ MVC ships
  // its knowledge test in 13 languages including Turkish; strong fit for the
  // Turkish-speaking US-immigrant community (NJ, NY, eastern US).
  { code: "tr", englishLabel: "Turkish", nativeLabel: "Türkçe", shortLabel: "TR", dir: "ltr", flagRegion: "TR", status: "ga" },
  // Wave 10+ — slot reserved; flip to "ga" when the bundle lands. Acts as the
  // pending-status canary for the locale-pending tests.
  { code: "vi", englishLabel: "Vietnamese", nativeLabel: "Tiếng Việt", shortLabel: "VI", dir: "ltr", flagRegion: "VN", status: "pending" },
] as const satisfies readonly LocaleDefinition[];

/**
 * The locale codes a user can select today. Excludes any locale whose
 * `status` is `"pending"` — those are reserved slots, not user-visible.
 *
 * Cast to `SupportedLocaleCode[]` because TypeScript can't narrow a
 * runtime `.filter()` callback back to a literal union; the locale-parity
 * test asserts the runtime contents match `SupportedLocaleCode`.
 */
export const SUPPORTED_LOCALES = LOCALE_DEFINITIONS
  .filter((l) => l.status === "ga")
  .map((l) => l.code) as readonly SupportedLocaleCode[];

/**
 * Every code, regardless of status. Used by the parity-test scaffold (which
 * must skip `pending` rows) and by tooling that wants to enumerate the slot
 * roster, not just GA locales.
 */
export const ALL_LOCALE_CODES = LOCALE_DEFINITIONS.map((l) => l.code) as readonly LocaleCode[];

/**
 * The fallback locale when nothing else matches (browser language, persisted
 * choice). Centralised here so a future redesign that pivots the default
 * (e.g. to Spanish for a regional rollout) only changes one line.
 */
export const FALLBACK_LOCALE: SupportedLocaleCode = "en";

/**
 * Type-safe lookup. Returns the full definition or `undefined`.
 */
export function getLocaleDefinition(code: string): LocaleDefinition | undefined {
  return LOCALE_DEFINITIONS.find((l) => l.code === code);
}

/**
 * Narrow check — is the given string a GA-status supported locale?
 *
 * Returns `false` for `pending` slot codes; use `getLocaleDefinition` (which
 * looks at `LOCALE_DEFINITIONS` directly) if you specifically need to
 * inspect a reserved slot.
 */
export function isSupportedLocale(code: string): code is SupportedLocaleCode {
  return (SUPPORTED_LOCALES as readonly string[]).includes(code);
}

/**
 * Best-effort match against a BCP-47 tag from `navigator.language` or an
 * `Accept-Language` header. Tries exact match first (so `zh-CN` is preferred
 * over `zh`), then falls back to primary-subtag match (so `es-MX` resolves
 * to `es`).
 *
 * Returns `undefined` if no supported locale matches — callers decide
 * whether to fall back to `FALLBACK_LOCALE` or persist `undefined` to mean
 * "user has not chosen yet".
 */
export function matchSupportedLocale(tag: string): SupportedLocaleCode | undefined {
  if (!tag) return undefined;
  const normalized = tag.trim();
  if (!normalized) return undefined;
  const lowered = normalized.toLowerCase();

  // (1) Exact match (case-insensitive on the region subtag).
  const exact = SUPPORTED_LOCALES.find(
    (code) => code.toLowerCase() === lowered,
  );
  if (exact) return exact;

  // (2) Primary-subtag-of-input match. `es-MX` → `es`. Used when the input
  // carries a region subtag we don't ship.
  const inputPrimary = lowered.split("-")[0];
  if (!inputPrimary) return undefined;
  const primaryExact = SUPPORTED_LOCALES.find(
    (code) => code.toLowerCase() === inputPrimary,
  );
  if (primaryExact) return primaryExact;

  // (3) Primary-subtag-of-supported match. `zh` → `zh-CN`. Used when the
  // browser sends the bare primary subtag and the only locale we ship is a
  // regional variant. Pick the first one in `LOCALE_DEFINITIONS` order so
  // the choice is deterministic.
  return SUPPORTED_LOCALES.find(
    (code) => code.toLowerCase().split("-")[0] === inputPrimary,
  );
}
